import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { CommandMenu, openCommandMenu } from '@/components/command-menu';
import { useAuth } from '@/lib/auth-context';
import { pages } from '@/router';

const DEFAULT_NAV_WIDTH = 224; // w-56
const MIN_NAV_PERCENT = 0.08;
const MAX_NAV_PERCENT = 0.2;
type NavPage = (typeof pages)[number];
type NavGroup = { key: string; order: number; section?: string; pages: NavPage[] };

export const AppLayout = () => {
  const { user, loading, signOut } = useAuth();
  const [navWidth, setNavWidth] = useState(DEFAULT_NAV_WIDTH);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileNavPinned, setMobileNavPinned] = useState(false);
  const [desktopNavOpen, setDesktopNavOpen] = useState(true);
  const location = useLocation();

  // Close mobile nav on route change
  useEffect(() => {
    if (!mobileNavPinned) {
      setMobileNavOpen(false);
    }
  }, [location.pathname, mobileNavPinned]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    const handleMouseMove = (ev: MouseEvent) => {
      const minW = Math.round(window.innerWidth * MIN_NAV_PERCENT);
      const maxW = Math.round(window.innerWidth * MAX_NAV_PERCENT);
      setNavWidth(Math.max(minW, Math.min(ev.clientX, maxW)));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='size-8 animate-spin rounded-full border-4 border-neutral-200 border-t-orange-500' />
      </div>
    );
  }

  const navGroups = pages
    .reduce<NavGroup[]>((groups, page) => {
      if (page.section == null) {
        groups.push({ key: page.path, order: page.order, pages: [page] });
        return groups;
      }

      const existingGroup = groups.find((group) => group.section === page.section);
      if (existingGroup) {
        existingGroup.pages.push(page);
        existingGroup.order = Math.min(existingGroup.order, page.order);
        return groups;
      }

      groups.push({ key: `section:${page.section}`, order: page.order, section: page.section, pages: [page] });
      return groups;
    }, [])
    .map((group) => ({ ...group, pages: [...group.pages].sort((a, b) => a.order - b.order) }))
    .sort((a, b) => a.order - b.order);

  const navLinkClass = (isActive: boolean, nested = false) =>
    `block truncate rounded-md px-3 py-2 text-sm ${nested ? 'pl-5' : ''} ${
      isActive ? 'font-mediumbold bg-neutral-50 text-neutral-900' : 'hover:bg-neutral-25 text-neutral-600'
    }`;

  const isPageActive = (page: NavPage) =>
    page.path === '/'
      ? location.pathname === '/'
      : location.pathname === page.path || location.pathname.startsWith(`${page.path}/`);

  const navContent = (
    <>
      <div className='flex flex-1 flex-col gap-3'>
        {navGroups.map((group) => {
          const rootPage = group.pages.find((page) => page.sectionRoot);
          const childPages = rootPage ? [rootPage, ...group.pages.filter((page) => page !== rootPage)] : group.pages;
          const groupIsActive = group.pages.some(isPageActive);

          return group.section == null ? (
            <ul key={group.key} className='flex flex-col gap-1'>
              {group.pages.map((page) => (
                <li key={page.path}>
                  <NavLink to={page.path} end={page.path === '/'} className={({ isActive }) => navLinkClass(isActive)}>
                    {page.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          ) : (
            <div key={group.key}>
              {rootPage ? (
                <NavLink to={rootPage.path} className={() => navLinkClass(groupIsActive)}>
                  {group.section}
                </NavLink>
              ) : (
                <p className='px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400'>
                  {group.section}
                </p>
              )}
              <ul className='flex flex-col gap-1'>
                {childPages.map((page) => (
                  <li key={page.path}>
                    <NavLink
                      to={page.path}
                      end={page.path === '/'}
                      className={({ isActive }) => navLinkClass(isActive, true)}
                    >
                      {page.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {user && (
        <div className='border-t border-neutral-100 pt-3'>
          <p className='truncate text-xs text-neutral-500'>{user.email}</p>
          <button onClick={signOut} className='mt-1 text-xs text-neutral-400 hover:text-neutral-600'>
            Sign out
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className='flex h-screen flex-col md:flex-row'>
      {/* Mobile header */}
      <div className='bg-neutral-0 sticky top-0 z-30 flex items-center gap-2 border-b border-neutral-100 p-3 md:hidden'>
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className='rounded-md p-1.5 text-neutral-600 hover:bg-neutral-50'
          aria-label='Toggle navigation'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='size-5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={2}
          >
            {mobileNavOpen ? (
              <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
            ) : (
              <path strokeLinecap='round' strokeLinejoin='round' d='M4 6h16M4 12h16M4 18h16' />
            )}
          </svg>
        </button>
        <span className='font-mediumbold text-sm text-neutral-900'>Dev Console</span>
        <button
          className='ml-auto flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-neutral-500 hover:border-neutral-300 hover:text-neutral-700'
          onClick={openCommandMenu}
          type='button'
        >
          <span>Search</span>
          <kbd className='rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] text-neutral-400'>
            Cmd K
          </kbd>
        </button>
      </div>

      <CommandMenu />

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <>
          {!mobileNavPinned && (
            <div className='fixed inset-0 z-40 bg-black/30 md:hidden' onClick={() => setMobileNavOpen(false)} />
          )}
          <nav className='bg-neutral-0 fixed inset-y-0 left-0 z-50 flex w-64 flex-col p-4 shadow-lg md:hidden'>
            <div className='mb-6 flex items-center justify-between gap-3'>
              <h1 className='font-mediumbold truncate text-lg text-neutral-900'>Dev Console</h1>
              <div className='flex shrink-0 items-center gap-1'>
                <button
                  aria-label={mobileNavPinned ? 'Unpin navigation drawer' : 'Pin navigation drawer'}
                  aria-pressed={mobileNavPinned}
                  className={`rounded-md p-1.5 ${
                    mobileNavPinned
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
                  }`}
                  onClick={() => setMobileNavPinned((isPinned) => !isPinned)}
                  title={mobileNavPinned ? 'Unpin navigation drawer' : 'Pin navigation drawer'}
                  type='button'
                >
                  <svg className='size-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M14.5 4.75l4.75 4.75-3.5.75-3 3 1 3.75-1 1-3.75-3.75-3.5 3.5-1.25-1.25 3.5-3.5L4 9.25l1-1 3.75 1 3-3 .75-3.5z'
                    />
                  </svg>
                </button>
                <button
                  aria-label='Close navigation drawer'
                  className='rounded-md p-1.5 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
                  onClick={() => {
                    setMobileNavPinned(false);
                    setMobileNavOpen(false);
                  }}
                  title='Close navigation drawer'
                  type='button'
                >
                  <svg className='size-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>
            {navContent}
          </nav>
        </>
      )}

      {/* Desktop sidebar */}
      {desktopNavOpen && (
        <nav
          className='bg-neutral-0 relative hidden shrink-0 flex-col border-r border-neutral-100 p-4 md:flex'
          style={{ width: navWidth }}
        >
          <div className='mb-6 space-y-3'>
            <h1 className='font-mediumbold truncate text-lg text-neutral-900'>Dev Console</h1>
            <button
              className='flex w-full items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-neutral-500 hover:border-neutral-300 hover:text-neutral-700'
              onClick={openCommandMenu}
              type='button'
            >
              <span>Search</span>
              <kbd className='rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] text-neutral-400'>
                Cmd K
              </kbd>
            </button>
          </div>
          {navContent}
          <button
            aria-label='Collapse sidebar'
            className='absolute right-3 top-4 rounded-md px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-50 hover:text-neutral-700'
            onClick={() => setDesktopNavOpen(false)}
            title='Collapse sidebar'
            type='button'
          >
            <svg className='size-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
            </svg>
          </button>
          {/* Drag handle */}
          <div
            onMouseDown={handleMouseDown}
            className='absolute inset-y-0 right-0 w-1.5 cursor-col-resize hover:bg-blue-400/40 active:bg-blue-400/60'
          />
        </nav>
      )}

      {!desktopNavOpen && (
        <div className='bg-neutral-0 hidden shrink-0 border-r border-neutral-100 p-2 md:block'>
          <button
            aria-label='Expand sidebar'
            className='rounded-md px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
            onClick={() => setDesktopNavOpen(true)}
            title='Expand sidebar'
            type='button'
          >
            <svg className='size-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
            </svg>
          </button>
        </div>
      )}

      <main className='min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-3 pb-12 md:p-6'>
        <Outlet />
      </main>
    </div>
  );
};
